import type { ChangeEventHandler } from 'react';
import { memo, useCallback } from 'react';

import type { KcContext } from 'keycloakify/login/KcContext';

import { Form } from 'src/shared/components/form/form.js';
import { Input } from 'src/shared/components/input/input.js';
import { useFieldMessage } from 'src/shared/hooks/use-field-message.js';
import { useFormValidator } from 'src/shared/hooks/use-form-validator.js';
import { useSavedFieldLs } from 'src/shared/hooks/use-saved-field-ls.js';
import { useGetMsg } from 'src/shared/utils/kc-messages.js';
import { redirectToSameUrlWithoutTab } from 'src/shared/utils/redirect-to-same-url-without-tab.js';

import { cnLogin } from './login.constants.js';

import './login.css';

const validationFields = ['username', 'password'];

export interface LoginProps {
    kcContext: KcContext.Login;
}

export const Login = memo<LoginProps>(({ kcContext }) => {
    const { realm, url, login } = kcContext;
    const { msgStr, advancedMsgStr } = useGetMsg(kcContext);

    const [onChangeSavedField, getSavedField] = useSavedFieldLs();

    const emailError = useFieldMessage(['email', 'username'], kcContext);
    const passwordError = useFieldMessage(['password'], kcContext);
    const [formErrors, resetFieldError, onSubmitValidated, submitDisabled] = useFormValidator(validationFields, {
        username: emailError,
        password: passwordError,
    });

    const redirectToLogin = useCallback<ChangeEventHandler<HTMLInputElement>>(
        (e) => {
            onChangeSavedField(e);
            redirectToSameUrlWithoutTab();
        },
        [onChangeSavedField],
    );

    return (
        <Form action={url.loginAction} kcContext={kcContext} onSubmit={onSubmitValidated}>
            <Input
                type="text"
                id="username"
                name="username" // Keycloak needs it!!!
                defaultValue={login.username ?? getSavedField('email')}
                label={msgStr('email')}
                autoComplete="email"
                autoFocus={!emailError}
                errorMessage={advancedMsgStr(formErrors['email'] ?? '')}
                onFocus={resetFieldError}
                onChange={redirectToLogin}
            />

            <Input
                type="password"
                id="password"
                name="password"
                label={msgStr('password')}
                autoComplete="off"
                autoFocus={true}
                errorMessage={advancedMsgStr(formErrors['password'] ?? '')}
                onFocus={resetFieldError}
            />

            <Input hidden type="checkbox" id="rememberMe" name="rememberMe" checked />

            {/* 'toThePlatform' is a custom message key not in the default union; cast to any until custom i18n extension typing is wired */}
            <Input type="submit" value={msgStr('toThePlatform')} disabled={submitDisabled} />

            {realm.resetPasswordAllowed && (
                <a className={cnLogin('reset-password-link')} href={url.loginResetCredentialsUrl}>
                    {msgStr('doForgotPassword')}
                </a>
            )}
        </Form>
    );
});
